import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import CssBaseline from '@mui/material/CssBaseline';

import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NuevoPresupuesto from './pages/NuevoPresupuesto';
import EditorPresupuesto from './pages/EditorPresupuesto';
import CatalogoMateriales from './pages/CatalogoMateriales';
import PreciosUnitarios from './pages/PreciosUnitarios';
import Configuracion from './pages/Configuracion';
import TestExport from './pages/TestExport';
import ConfiguracionPlantillas from './pages/ConfiguracionPlantillas';
import DiseñadorPlantillas from './pages/DiseñadorPlantillas';
import GestionPlantillas from './pages/GestionPlantillas';
import AdminUsuarios from './pages/AdminUsuarios';
import AdminDashboard from './pages/AdminDashboard';
import API_URL from './config/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ requiredRole }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.rol !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check authentication status
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token is still valid
      fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      Cargando...
    </div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public Route */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/" replace /> : <Login />
            } />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/nuevo-presupuesto" element={<NuevoPresupuesto />} />
              <Route path="/presupuesto/:id" element={<EditorPresupuesto />} />
              <Route path="/catalogo-materiales" element={<CatalogoMateriales />} />
              <Route path="/precios-unitarios" element={<PreciosUnitarios />} />
              <Route path="/configuracion" element={<Configuracion />} />
              <Route path="/configuracion-plantillas" element={<ConfiguracionPlantillas />} />
              <Route path="/diseñador-plantillas" element={<DiseñadorPlantillas />} />
              <Route path="/gestion-plantillas" element={<GestionPlantillas />} />
              <Route path="/test-export" element={<TestExport />} />
            </Route>

            {/* Admin Only Routes */}
            <Route element={<ProtectedRoute requiredRole="admin" />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/usuarios" element={<AdminUsuarios />} />
            </Route>
          </Routes>
        </Router>
        <Toaster position="top-right" />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;